import { memo, useCallback, useMemo } from 'react';
import {
  MessageSquare,
  Paperclip,
  Link2,
  CheckSquare,
  Clock,
  Ban as BanIcon,
  Eye,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { PlayerProfile } from '../PlayerProfile';
import { CreatorProfile } from '../CreatorProfile';
import { cn } from '../../utils/cn';
import type { KanbanCardSummary } from '../../types/kanban';
import { CLASS_OF_SERVICE_CONFIG } from '../../types/kanban';
import { KanbanCardTypeBadge } from './KanbanCardTypeBadge';
import { KanbanPriorityBadge } from './KanbanPriorityBadge';
import { KanbanClassOfServiceIndicator } from './KanbanClassOfServiceIndicator';

export interface KanbanCardMiniProps {
  card: KanbanCardSummary;
  onClick: (cardId: string) => void;
  wipWarningHours?: number;
  className?: string;
  /**
   * When the board has swimlanes but is shown in column mode, show which lane the card belongs to.
   */
  swimlaneBadge?: { title: string; color: string | null } | null;
  /** When false, native drag is disabled (e.g. read-only board). Defaults to true. */
  draggable?: boolean;
  /** Called after card id is written to `dataTransfer`; use to track drag state. */
  onDragStart?: (e: React.DragEvent, cardId: string) => void;
  onDragEnd?: (e: React.DragEvent, cardId: string) => void;
}

const DEFAULT_WIP_WARNING_HOURS = 48;

function CountChip({
  icon: Icon,
  count,
  suffix,
}: {
  icon: typeof MessageSquare;
  count: number;
  suffix?: string;
}) {
  if (count <= 0 && !suffix) return null;
  return (
    <span className="inline-flex items-center gap-0.5 tabular-nums">
      <Icon className="w-3 h-3 shrink-0" />
      {suffix ?? count}
    </span>
  );
}

export const KanbanCardMini = memo(
  ({
    card,
    onClick,
    wipWarningHours = DEFAULT_WIP_WARNING_HOURS,
    className,
    swimlaneBadge,
    draggable = true,
    onDragStart,
    onDragEnd,
  }: KanbanCardMiniProps) => {
    const ageMs = Date.now() - card.list_entered_at;
    const ageHours = ageMs / (1000 * 60 * 60);
    const halfWarn = wipWarningHours * 0.5;
    const wipDotClass =
      ageHours >= wipWarningHours
        ? 'bg-red-400'
        : ageHours >= halfWarn
          ? 'bg-amber-400'
          : 'bg-emerald-400';

    const isOverdue =
      card.due_date != null && card.due_date < Date.now() && card.completed_at == null;

    const dueLabel = useMemo(() => {
      if (card.due_date == null) return null;
      return `Due ${formatDistanceToNow(card.due_date, { addSuffix: true })}`;
    }, [card.due_date]);

    const handleDragStart = useCallback(
      (e: React.DragEvent) => {
        if (!draggable) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', card.id);
        e.dataTransfer.setData('application/x-kanban-card-id', card.id);
        onDragStart?.(e, card.id);
      },
      [card.id, draggable, onDragStart],
    );

    const handleDragEnd = useCallback(
      (e: React.DragEvent) => {
        if (!draggable) return;
        onDragEnd?.(e, card.id);
      },
      [card.id, draggable, onDragEnd],
    );

    const handleClick = useCallback(() => {
      onClick(card.id);
    }, [card.id, onClick]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(card.id);
        }
      },
      [card.id, onClick],
    );

    const assignees = card.assignees;
    const overflowAssignees = Math.max(0, assignees.length - 3);

    const cosBorder = CLASS_OF_SERVICE_CONFIG[card.class_of_service].border;

    return (
      <div
        data-kanban-card={card.id}
        role="button"
        tabIndex={0}
        draggable={draggable}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative glass-card cursor-pointer group overflow-hidden transition-colors duration-150',
          'hover:border-accent-400/35 hover:bg-glass-100/5',
          card.class_of_service === 'expedite' && 'border-l-2 border-red-500',
          card.class_of_service === 'fixed_date' && 'border-l-2 border-orange-500',
          card.class_of_service === 'intangible' && cn('border border-dashed opacity-75', cosBorder),
          card.is_blocked === 1 && 'border-l-2 border-red-500 ring-1 ring-inset ring-red-500/25 bg-red-500/5',
          className,
        )}
      >
        <span
          className={cn(
            'absolute z-[1] h-2 w-2 rounded-full',
            card.cover_color ? 'right-2 top-3' : 'right-2 top-2',
            wipDotClass,
          )}
          title={`~${ageHours.toFixed(1)}h in list`}
          aria-hidden
        />

        {card.cover_color ? (
          <div className="h-1 w-full shrink-0" style={{ backgroundColor: card.cover_color }} />
        ) : null}

        <div className="relative p-2.5 space-y-1.5 pr-7">
          {swimlaneBadge ? (
            <div
              className="flex items-center gap-1.5 min-w-0 text-[10px] font-medium text-glass-400"
              title={swimlaneBadge.title}
            >
              {swimlaneBadge.color ? (
                <span
                  className="h-1.5 w-1.5 rounded-full shrink-0 ring-1 ring-white/10"
                  style={{ backgroundColor: swimlaneBadge.color }}
                  aria-hidden
                />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full shrink-0 bg-glass-500 ring-1 ring-white/10" aria-hidden />
              )}
              <span className="truncate">{swimlaneBadge.title}</span>
            </div>
          ) : null}
          <div className="flex items-start gap-2">
            <KanbanCardTypeBadge type={card.card_type} showLabel={false} className="!px-1.5 !py-0 !text-[10px] shrink-0" />
            <h4 className="flex-1 min-w-0 text-sm font-medium text-white line-clamp-2 leading-snug">{card.title}</h4>
            {card.priority !== 5 ? (
              <KanbanPriorityBadge priority={card.priority} className="!px-1.5 !py-0 !text-[10px] shrink-0" />
            ) : null}
          </div>

          {card.labels.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {card.labels.map((l) => (
                <span
                  key={l.id}
                  className="px-1.5 py-0 rounded-md text-[10px] font-medium leading-4 max-w-full truncate"
                  style={{ backgroundColor: `${l.color}25`, color: l.color }}
                >
                  {l.name}
                </span>
              ))}
            </div>
          ) : null}

          {card.is_blocked === 1 ? (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-red-500/25 bg-red-500/10 text-red-300 text-[11px]">
              <BanIcon className="w-3.5 h-3.5 shrink-0" />
              <span className="min-w-0 truncate">{card.blocked_reason ?? 'Blocked'}</span>
            </div>
          ) : null}

          {card.class_of_service !== 'standard' ? (
            <div className="flex justify-start">
              <KanbanClassOfServiceIndicator classOfService={card.class_of_service} />
            </div>
          ) : null}

          {card.created_by?.trim() ? (
            <div className="flex items-center gap-1.5 min-w-0 text-[10px] text-glass-500 pointer-events-none">
              <span className="shrink-0">Created by</span>
              <span className="min-w-0 truncate [&_a]:!text-[10px] [&_span]:!font-medium [&_span]:!text-glass-300">
                <CreatorProfile creatorId={card.created_by.trim()} inline />
              </span>
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-2 text-xs text-glass-400">
            <div className="flex items-center -space-x-1.5 min-w-0 pointer-events-none">
              {assignees.slice(0, 3).map((a) => (
                <div
                  key={`${a.user_uuid}-${a.assigned_at}`}
                  className="relative z-0 first:z-[3] [&:nth-child(2)]:z-[2] [&:nth-child(3)]:z-[1] w-5 h-5 shrink-0 rounded-full ring-2 ring-glass-900/80 overflow-hidden bg-glass-800 [&_a]:!flex [&_a]:items-center [&_a]:justify-center [&_a]:!w-5 [&_a]:!h-5 [&_a]:!min-w-0 [&_a]:!p-0 [&_a]:space-x-0 [&_.flex-1]:hidden [&_img]:!w-5 [&_img]:!h-5 [&_img]:!rounded-full"
                  title={a.user_uuid}
                >
                  <PlayerProfile uuid={a.user_uuid} showUuid={false} />
                </div>
              ))}
              {overflowAssignees > 0 ? (
                <span className="ml-2 text-[10px] font-medium text-glass-300 tabular-nums">+{overflowAssignees}</span>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 shrink-0">
              <CountChip icon={MessageSquare} count={card.comment_count} />
              <CountChip icon={Paperclip} count={card.attachment_count} />
              <CountChip icon={Link2} count={card.link_count} />
              <CountChip icon={Eye} count={card.watcher_count} />
              {card.checklist_progress.total > 0 ? (
                <CountChip
                  icon={CheckSquare}
                  count={card.checklist_progress.total}
                  suffix={`${card.checklist_progress.checked}/${card.checklist_progress.total}`}
                />
              ) : null}
            </div>
          </div>

          {card.checklist_progress.total > 0 ? (
            <div className="space-y-1">
              <div className="inline-flex items-center gap-1 text-[10px] text-glass-300 tabular-nums">
                <CheckSquare className="w-3 h-3" aria-hidden />
                {card.checklist_progress.checked}/{card.checklist_progress.total}
              </div>
              <div className="h-1 w-full rounded-full bg-[var(--surface-inset)] overflow-hidden">
                <div
                  className="h-full rounded-full bg-accent-400/80 transition-all"
                  style={{
                    width: `${Math.round((100 * card.checklist_progress.checked) / card.checklist_progress.total)}%`,
                  }}
                />
              </div>
            </div>
          ) : null}

          {dueLabel ? (
            <div
              className={cn(
                'inline-flex items-center gap-1 text-xs min-w-0',
                isOverdue ? 'text-red-400 font-medium' : 'text-glass-400',
              )}
            >
              <Clock className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{dueLabel}</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

KanbanCardMini.displayName = 'KanbanCardMini';
